// src/data/timeLimit.js
//
// RULING 314 - DAY ONE IS THE DAY OF THE EVENT (Bob 2026-10-14). The one place a time limit's LAST DAY is computed.
//
// Bob, verbatim: "day one of an alleged carrier rule violation is the day of discovery, the day the carrier receives a
// report about an undiscovered damage to a rail car or track switch, the day it is observed by the carrier official,
// that's day one of the time limits. For our intents and purposes, day one is the day of the event and the last day the
// carrier can receive the claim in the timely manner defined by the cba is ussually 29 or 59 days later".
//
// Proved on his own cases (Drive, opakeeley@gmail.com, read 2026-10-14):
//   - Battersbee, Amtrak file 06.050 (the Organization's procedural brief, "katawba battersbee 5.doc"): the supervisor's
//     first knowledge was January 31, 2006 - day one - and the letter of intent was hand delivered March 2, 2006, "a
//     total of 31 calendar days, one full day in excess of the time limits" of Rule 36(a)'s 30. The same brief puts the
//     30-day hearing limit at "no later than March 1, 2006" - January 31 + 29.
//   - Guinan, PLB 6631 Award No. 2 (Zusman, January 2007): the Organization's appeal, due within 30 calendar days of the
//     decision, came 63 days later; "The claim is barred and must be dismissed without consideration of the merits."
//     The limit binds both parties, and a day is decisive.
// The platform used to add the whole count (event + 60), which put every last day ONE DAY LATE - the dangerous direction:
// a member filing on the day he was told was his last would be untimely.
//
// TIMELINESS IS RECEIPT. What a limit measures is the day the other party RECEIVED the document - hand delivered (signed
// for and dated), sent electronically with proof of delivery (e-mail; special carrier, Uber or bike same-day delivery),
// or by USPS certified mail (next-day delivery) - not the date typed on it (Battersbee: dated March 1, received March 2).
//
// MONTHS (forensic audit 2026-10-16). Months were not handled here, and two private reckonings grew up: the discipline
// ladder's addMonths (setUTCMonth, which OVERFLOWS a month end - a decision of 2026-08-31 plus six months came out
// 2027-03-03) and claimRequirements' days: 183 stand-in (2026-01-01 came out 2026-07-02). Both ran LATE. Every `months`
// clock now goes through lastDayOfMonths below, and the rule is ruling 314's carried over to months:
//   an N-day limit counts day one as day 1, so it ends N - 1 days after day one;
//   an N-month limit counts day one as the first day of the first month, so it ends THE DAY BEFORE the same calendar
//   date N months later - and where that month has no such date (the 29th-31st into a shorter month) the date does not
//   exist to stand on, so the limit ends on that month's LAST day.
// So six months from 2026-01-01 ends 2026-06-30 (the day before 2026-07-01); from 2026-03-01, 2026-08-31; from 2026-08-31
// there is no 2027-02-31, so it ends 2027-02-28. Never later than the overflowing setUTCMonth date it replaces. (A
// "day zero + N months" rule was tried and refused: from 2026-03-01 it gives 2026-08-28, three days short of six months.)

const isDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ''));

// The last day of an N-day limit whose day one is `dayOne` (YYYY-MM-DD): dayOne + (N - 1). null when either is unknown.
export function lastDayOf(dayOne, days) {
  if (!isDate(dayOne) || !Number.isFinite(days) || days < 1) return null;
  const t = new Date(String(dayOne) + 'T00:00:00Z');
  t.setUTCDate(t.getUTCDate() + Math.floor(days) - 1);
  return t.toISOString().slice(0, 10);
}

// The last day of an N-calendar-month limit whose day one is `dayOne` (YYYY-MM-DD): the day before the same date N months
// later, or that month's last day where it has no such date (see MONTHS above). null when either is unknown. Used for
// every `months` clock (6 months, 12 months).
export function lastDayOfMonths(dayOne, months) {
  if (!isDate(dayOne) || !Number.isFinite(months) || months < 1) return null;
  const [y0, m0, d0] = String(dayOne).split('-').map(Number);
  const total = (m0 - 1) + Math.floor(months);
  const y = y0 + Math.floor(total / 12), m = total % 12;
  const monthEnd = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  if (d0 > monthEnd) return new Date(Date.UTC(y, m, monthEnd)).toISOString().slice(0, 10);
  return new Date(Date.UTC(y, m, d0 - 1)).toISOString().slice(0, 10);   // Date.UTC rolls day 0 back to the prior month's end
}

// The last day of a clock that carries either `months` or `days` (claimRequirements.js clocks): months win where the
// agreement states months - a `days` figure beside them is only the old approximation (183, 365) and is never counted.
export function lastDayOfClock(dayOne, clock) {
  const c = clock || {};
  if (Number.isFinite(c.months)) return lastDayOfMonths(dayOne, c.months);
  return lastDayOf(dayOne, c.days);
}

// Which day of the limit a given day is (day one = 1). null when unknown.
export function dayNumber(dayOne, onDay) {
  if (!isDate(dayOne) || !isDate(onDay)) return null;
  return Math.round((Date.parse(onDay + 'T00:00:00Z') - Date.parse(dayOne + 'T00:00:00Z')) / 86400000) + 1;
}

export const DAY_ONE_RULE = 'Day one is the day of the event (or, in discipline, of discovery); the last day of an N-day limit is N - 1 days later.';
export const MONTH_RULE = 'An N-month limit ends the day before the same date N months later - or on that month\'s last day where it has no such date.';
